/* eslint-disable camelcase */
/* eslint-disable max-depth */
import { Auth } from 'aws-amplify';
import { defineMessage } from '@lingui/macro';
import * as V from '../constants/variables';

/**
 * Группирует регионы по странам с приоритетом для Russia, Belarus, Kazakhstan
 * @param {Array} regions - массив регионов
 * @returns {Array} массив групп регионов
 */
export const groupRegionsByCountry = regions => {
    if (!Array.isArray(regions) || regions.length === 0) {
        return [];
    }

    // Приоритет стран
    const countryPriority = [ 'Russia', 'Belarus', 'Kazakhstan' ];
    // Группируем по странам
    const grouped = regions.reduce((acc, region) => {
        const country = region.country || 'Other';
        if (!acc[country]) {
            acc[country] = [];
        }
        acc[country].push(region);
        return acc;
    }, {});
    // Сортируем страны по приоритету
    const sortedCountries = Object.keys(grouped).sort((a, b) => {
        const aIndex = countryPriority.indexOf(a);
        const bIndex = countryPriority.indexOf(b);

        // Если обе страны в приоритете, сортируем по индексу
        if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
        }

        // Если только одна в приоритете, она идет первой
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        // Остальные сортируем по алфавиту
        return a.localeCompare(b);
    });
    // Сортируем города внутри каждой страны
    const result = sortedCountries.map(country => ({
        country,
        regions: grouped[country].sort((a, b) => {
            // Получаем строковое представление имени для сортировки
            const nameA = typeof a.name === 'string' ? a.name : a.name.id || a.name.message || '';
            const nameB = typeof b.name === 'string' ? b.name : b.name.id || b.name.message || '';
            return nameA.localeCompare(nameB);
        }),
    }));

    return result;
};

/**
 * Загружает регионы из API
 * @param {Function} setRegions - функция для установки регионов в состояние
 * @param {Function} dispatch - dispatch функция для обработки ошибок
 */
export const fetchRegions = (setRegions, dispatch) => {
    Auth.currentSession().then(async session => {
        const token = session.getIdToken().getJwtToken();

        try {
            const url = `${V.API_GW_URL}/regions-list`;
            const response = await fetch(url, {
                headers: {
                    Authorization: token,
                },
            });

            if (response.ok) {
                try {
                    const data = await response.json();
                    // Парсим JSON из поля body, если данные приходят в таком формате
                    const locations = typeof data.body === 'string'
                        ? JSON.parse(data.body).locations
                        : data.locations || data;

                    if (Array.isArray(locations) && locations.length > 0) {
                        // Фильтруем валидные локации
                        const validLocations = locations.filter(location => location.city &&
                            typeof location.lon === 'number' &&
                            typeof location.lat === 'number'
                        );

                        if (validLocations.length > 0) {
                            // Преобразуем данные в нужный формат
                            const transformedRegions = validLocations.map(location => ({
                                id: location.city_id,
                                name: defineMessage({ message: location.city }),
                                name_en: location.city_en,
                                lon: location.lon,
                                lat: location.lat,
                                country: location.country || 'Other',
                            }));

                            setRegions(transformedRegions);
                            return;
                        }
                    }
                } catch (parseError) {
                    // Ошибка парсинга
                }
            }
        } catch (fetchError) {
            // Ошибка запроса
        }

        // Если API не отвечает, устанавливаем пустой массив
        setRegions([]);
    }).catch(authError => {
        // Если ошибка авторизации, устанавливаем пустой массив
        setRegions([]);
    });
};
